import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { BlockContentDeleted } from '@ContractBuilder/modules/block-content/views/BlockContentDeleted';
import { ctxIsPDF } from '@ContractBuilder/rules/block/is-ctx';
import { useCommentsUIStore } from '@ContractBuilder/store/comments.store';
import { getSegmentAnchorOffset, projectAnchorsOntoSegment, useBlockThreadAnchors } from '@features/comments';
import { InlineBlockEditController } from '@features/inline-block-edit';
import { VariationsSelectorLiteController } from '@features/variations-tabs';
import { useSyncVariationInView } from '@providers/variations-tabs';
import { anchorTextOf } from '@root/helpers/comments/anchor-text';
import { isEndorsementViewPath } from '@utils/app-paths';

import { BlockContent } from '../views/BlockContent';

export const BlockContentController = () => {
  const {
    block,
    isHighlighted,
    context,
    mentionValues,
    segment,
    details: {
      hasBeenMovedByRenewal,
      isParentEntityEditable,
      isVisible,
      isEditing,
      variations: { count },
    },
  } = useBlockFlags();

  const isPDFRender = ctxIsPDF(context);

  const isEndorsementView = isEndorsementViewPath();
  const syncVariationInView = useSyncVariationInView();
  const threadAnchors = useBlockThreadAnchors(block.id);
  const openThreads = useCommentsUIStore(({ openThreads }) => openThreads);

  const anchorOffset = useMemo(
    () => (segment ? getSegmentAnchorOffset(block.content ?? '', segment.index) : 0),
    [segment, block.content],
  );
  const segmentAnchors = useMemo(
    () =>
      segment && threadAnchors.length > 0
        ? projectAnchorsOntoSegment(threadAnchors, anchorOffset, anchorTextOf(segment.content).length)
        : threadAnchors,
    [segment, threadAnchors, anchorOffset],
  );

  useEffectOnce(() => {
    syncVariationInView(block);
  });

  if (isEditing) {
    return <InlineBlockEditController />;
  }

  const isContinuation = segment && !segment.isFirst;

  if (!isEndorsementView && !isPDFRender && isVisible && count && !isContinuation) {
    return <VariationsSelectorLiteController block={block} />;
  }

  if ((isEndorsementView || isPDFRender) && block?.isDeleted) {
    return <BlockContentDeleted block={block} isHighlighted={isHighlighted} isPdfRender={isPDFRender} />;
  }

  return (
    <BlockContent
      block={segment ? { ...block, content: segment.content } : block}
      disableMentionHighlight={!isParentEntityEditable && !isPDFRender}
      hasBeenMovedByRenewal={hasBeenMovedByRenewal}
      mentionValues={mentionValues}
      isEditing={isEditing}
      isHighlighted={isHighlighted}
      isInCreateMode={isEditing && !block?.id}
      isContinuation={isContinuation}
      isPdfRender={isPDFRender}
      isVisible={isVisible}
      showEndorsementDiff={isEndorsementView}
      anchorOffset={anchorOffset}
      threadAnchors={isPDFRender ? undefined : segmentAnchors}
      onThreadMarkClick={(threadIds) => openThreads(block.id, threadIds)}
    />
  );
};
